(() => {
  const pageNavSelector = 'main > :is(.sticky-navbar, .sticky-navbar-v2, .category-tab)';
  function measureStickyOffsets() {
    const header = document.querySelector('.speakon-header-section');
    const pageNav = [...document.querySelectorAll(pageNavSelector)].find(bar => bar.offsetHeight);
    document.documentElement.style.setProperty('--speakon-header-height', `${header?.offsetHeight || 0}px`);
    document.documentElement.style.setProperty('--speakon-page-nav-height', `${pageNav?.offsetHeight || 0}px`);
  }
  const stickyResize = new ResizeObserver(measureStickyOffsets);
  function observeStickyOffsets() {
    stickyResize.disconnect();
    document.querySelectorAll(`.speakon-header-section, ${pageNavSelector}`).forEach(element => stickyResize.observe(element));
    measureStickyOffsets();
  }
  observeStickyOffsets();
  document.addEventListener('shopify:section:load', observeStickyOffsets);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(observeStickyOffsets));

  function init(root = document) {
    root.querySelectorAll('.speakon-header').forEach((header) => {
      if (header.dataset.navigationReady) return;
      header.dataset.navigationReady = 'true';
      const section = header.closest('.speakon-header-section');
      const toggle = header.querySelector('.as-navbar-toggler');
      const nav = header.querySelector('.as-site-header');
      if (!toggle || !section || !nav) return;
      function sync() {
        const open = section.classList.contains('expanded');
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        toggle.classList.toggle('collapsed', !open);
        if (!window.matchMedia('(min-width: 1280px)').matches) {
          if (open) header.style.setProperty('--speakon-mobile-menu-top', `${Math.max(0, section.getBoundingClientRect().bottom)}px`);
          const expand = open ? 'false' : 'true';
          if (nav.dataset.expand !== expand) nav.dataset.expand = expand;
        }
      }
      function close() {
        section.classList.remove('expanded', 'dropdown-backdrop-show');
        nav.dataset.expand = 'true';
        document.body.classList.remove('overflow-hidden');
        header.querySelector('.as-dropdown-backdrop')?.classList.remove('show');
        header.querySelectorAll('.as-nav-link-container > li > button').forEach(button => {
          button.setAttribute('aria-expanded', 'false');
          button.parentElement.querySelector('.mega-menu-dropdown')?.classList.remove('show');
        });
        sync();
      }
      const observer = new MutationObserver(sync);
      observer.observe(section, { attributes: true, attributeFilter: ['class'] });
      observer.observe(nav, { attributes: true, attributeFilter: ['data-expand'] });
      header.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && section.classList.contains('expanded')) {
          close();
          toggle.focus();
        }
      });
      header.addEventListener('click', (event) => {
        const link = event.target.closest('a[href], [data-bs-toggle="offcanvas"], [data-bs-toggle="modal"]');
        if (link && !link.matches('[data-bs-toggle="dropdown"]')) close();
      });
      const breakpoint = window.matchMedia('(min-width: 1280px)');
      // Mobile disclosures are independent accordions, not Bootstrap popovers.
      header.addEventListener('click', event => {
        const button = event.target.closest('.as-nav-link-container > li > button');
        if (breakpoint.matches || !button) return;
        const panel = button.parentElement.querySelector('.mega-menu-dropdown');
        if (!panel) return;
        event.preventDefault();
        event.stopPropagation();
        panel.toggleAttribute('data-instant-motion', event.isTrusted && event.detail === 0);
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        panel.classList.toggle('show', open);
      });
      // Legacy desktop listeners survive viewport changes; mobile menus are click-only.
      ['mouseover', 'mouseout', 'mouseenter', 'mouseleave'].forEach(type => {
        header.addEventListener(type, event => {
          if (!breakpoint.matches && event.target.closest?.('.as-dropdown')) event.stopPropagation();
        }, true);
      });
      const menu = header.querySelector('.as-nav-link-container');
      if (menu) {
        const indicator = document.createElement('span');
        indicator.className = 'speakon-nav-indicator';
        indicator.setAttribute('aria-hidden', 'true');
        header.append(indicator);
        header.dataset.motionReady = 'true';
        let active = null;
        let leaveTimer;
        function show(item, instant = false) {
          if (item && !breakpoint.matches) return;
          clearTimeout(leaveTimer);
          header.toggleAttribute('data-instant-motion', instant);
          const origin = header.getBoundingClientRect();
          const link = item?.querySelector(':scope > .nav-link');
          if (link) {
            const rect = link.getBoundingClientRect();
            const translate = `${rect.left - origin.left}px 0`;
            const scale = `${rect.width} 1`;
            if (indicator.style.opacity !== '1') {
              indicator.style.transition = 'none';
              indicator.style.translate = translate;
              indicator.style.scale = scale;
              void indicator.offsetWidth;
              indicator.style.transition = '';
            }
            indicator.style.translate = translate;
            indicator.style.scale = scale;
          }
          indicator.style.opacity = link ? '1' : '0';
          active?.removeAttribute('data-motion-open');
          active?.querySelector(':scope > button')?.setAttribute('aria-expanded', 'false');
          active = item;
          const panel = item?.querySelector('.mega-menu-dropdown');
          if (panel) {
            item.setAttribute('data-motion-open', '');
            link.setAttribute('aria-expanded', 'true');
          }
        }
        menu.querySelectorAll(':scope > li').forEach(item => {
          item.addEventListener('pointerenter', event => {
            if (event.pointerType === 'mouse') show(item);
          });
          item.addEventListener('focusin', event => show(item, event.target.matches(':focus-visible')));
          item.querySelector(':scope > button')?.addEventListener('click', event => {
            if (breakpoint.matches) show(item, event.detail === 0);
          });
        });
        header.addEventListener('pointerleave', () => {
          leaveTimer = setTimeout(() => show(null), 100);
        });
        header.addEventListener('pointerenter', () => clearTimeout(leaveTimer));
        header.addEventListener('focusout', event => {
          if (!header.contains(event.relatedTarget)) show(null, true);
        });
        header.addEventListener('keydown', event => {
          if (event.key === 'Escape' && breakpoint.matches) {
            active?.querySelector(':scope > .nav-link')?.focus();
            show(null, true);
          }
        });
        const ariaObserver = new MutationObserver(() => {
          if (!breakpoint.matches) return;
          menu.querySelectorAll(':scope > li > button').forEach(button => {
            const expanded = String(button.parentElement === active);
            if (button.getAttribute('aria-expanded') !== expanded) button.setAttribute('aria-expanded', expanded);
          });
        });
        ariaObserver.observe(menu, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
        const resetMotion = () => show(null, true);
        breakpoint.addEventListener('change', resetMotion);
        const resize = new ResizeObserver(() => {
          sync();
          if (breakpoint.matches && active) show(active, true);
        });
        resize.observe(header);
        section.addEventListener('shopify:section:unload', () => {
          clearTimeout(leaveTimer);
          resize.disconnect();
          breakpoint.removeEventListener('change', resetMotion);
          ariaObserver.disconnect();
        }, { once: true });
      }
      breakpoint.addEventListener('change', close);
      section.addEventListener('shopify:section:unload', () => {
        close();
        breakpoint.removeEventListener('change', close);
        observer.disconnect();
      }, { once: true });
      sync();
    });
  }
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
